/**
 * src/shell/licensing/ActivationModal.tsx
 *
 * Sovereign Mesh Node — License Activation & Monetization Modal.
 * Supports:
 *  - 1-Click Online Tier Upgrade via Stripe / TNG / GrabPay (opens in browser)
 *  - Online License Key Activation
 *  - Offline Air-gapped Request/Response File Import (.mreq / .mlic)
 *  - Real-time Trial & Seat Status Display
 */

import React, { useState, useEffect, useCallback } from 'react';
import type {
  LicenseVerificationResult,
  OnlineActivationResponse,
  LicenseMode,
} from '../../licensing/licensing-contracts.js';

interface ActivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceUrl?: string; // default: https://activation.herx.org or http://localhost:8090
}

interface TierPlan {
  id: 'STANDARD' | 'PRO' | 'ENTERPRISE';
  name: string;
  priceUsd: string;
  priceMyr: string;
  devices: number;
  features: string[];
  recommended?: boolean;
}

const TIERS: TierPlan[] = [
  {
    id: 'STANDARD',
    name: 'Standard',
    priceUsd: '$29/mo',
    priceMyr: 'RM 129/mo',
    devices: 3,
    features: ['Mesh Relay', 'Data Plane', 'Sovereign Chat', 'Task Flows'],
  },
  {
    id: 'PRO',
    name: 'Pro',
    priceUsd: '$79/mo',
    priceMyr: 'RM 349/mo',
    devices: 10,
    features: ['All Standard Features', 'Gallery Plugin & Provenance', 'Local LLM Inference', 'POS Terminal', 'Live Inventory'],
    recommended: true,
  },
  {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    priceUsd: '$199/mo',
    priceMyr: 'RM 899/mo',
    devices: 20,
    features: ['All Pro Features', 'Gallery VIP Escrow', 'Distributed Storage', 'Sovereign VPN', 'Multi-tenant Payroll'],
  },
];


export const ActivationModal: React.FC<ActivationModalProps> = ({
  isOpen,
  onClose,
  serviceUrl = 'https://activation.herx.org',
}) => {
  const [status, setStatus] = useState<LicenseVerificationResult | null>(null);
  const [licenseKeyInput, setLicenseKeyInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [selectedRail, setSelectedRail] = useState<'stripe' | 'tng' | 'grabpay'>('stripe');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'upgrade' | 'key' | 'offline'>('upgrade');

  const fetchStatus = useCallback(async () => {
    try {
      if (window.licenseAPI?.getLicenseStatus) {
        const res = await window.licenseAPI.getLicenseStatus();
        setStatus(res);
      }
    } catch (err) {
      console.warn('[ActivationModal] Failed to read license status:', err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      setErrorMsg(null);
      setSuccessMsg(null);
    }
  }, [isOpen, fetchStatus]);

  if (!isOpen) return null;

  // ── Online Key Activation ──────────────────────────────────────────────────
  const handleActivateOnline = async () => {
    const key = licenseKeyInput.trim().toUpperCase();
    if (!key) {
      setErrorMsg('Please enter a valid license key (SOV-XXXX-XXXX-XXXX-XXXX).');
      return;
    }

    setIsLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      if (window.licenseAPI?.activateOnline) {
        const res = await window.licenseAPI.activateOnline(key);
        if (res.ok === false) {
          setErrorMsg(res.message || 'Activation failed. Please check your key.');
        } else if (res.ok === true) {
          setSuccessMsg('License successfully activated! Mesh capabilities unlocked.');
          await fetchStatus();
        }
      } else {
        setErrorMsg('License API bridge unavailable in this environment.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown activation error';
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Checkout Initiation (Stripe or SEA eWallets) ───────────────────────────
  const handleUpgradeCheckout = async (tier: TierPlan) => {
    const email = userEmail.trim() || 'operator@mesh.local';
    setIsLoading(true);
    setErrorMsg(null);

    try {
      let checkoutUrl = '';

      if (selectedRail === 'stripe') {
        const res = await fetch(`${serviceUrl}/api/v1/billing/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            appId: 'sovereign-mesh-node',
            priceId: `price_${tier.id.toLowerCase()}`, // or price ID mapping
            successUrl: `${serviceUrl}/billing/success?tier=${tier.id}`,
            cancelUrl: `${serviceUrl}/billing/cancel`,
          }),
        });
        const json = await res.json();
        if (json.ok && json.url) {
          checkoutUrl = json.url;
        } else {
          throw new Error(json.error || 'Failed to generate Stripe checkout session');
        }
      } else {
        // SEA Rails (TNG / GrabPay)
        const amountCents = tier.id === 'STANDARD' ? 12900 : tier.id === 'PRO' ? 34900 : 89900;
        const res = await fetch(`${serviceUrl}/api/v1/billing/checkout/sea`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            providerName: selectedRail,
            appId: 'sovereign-mesh-node',
            tier: tier.id,
            email,
            amountCents,
            currency: 'MYR',
            durationMonths: 1,
            successUrl: `${serviceUrl}/billing/success?provider=${selectedRail}`,
            cancelUrl: `${serviceUrl}/billing/cancel`,
          }),
        });
        const json = await res.json();
        if (json.ok && json.session?.url) {
          checkoutUrl = json.session.url;
        } else {
          throw new Error(json.error || `Failed to initiate ${selectedRail.toUpperCase()} payment`);
        }
      }

      if (checkoutUrl) {
        if (window.electronAPI?.openExternal) {
          await window.electronAPI.openExternal(checkoutUrl);
        } else {
          window.open(checkoutUrl, '_blank', 'noopener');
        }
        setSuccessMsg(`Payment window opened in your browser. License will automatically activate upon completion.`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Checkout error';
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Offline File Flow ──────────────────────────────────────────────────────
  const handleExportOfflineReq = async () => {
    const key = licenseKeyInput.trim().toUpperCase();
    if (!key) {
      setErrorMsg('Please enter your license key before exporting offline request.');
      return;
    }
    try {
      if (window.licenseAPI?.exportOfflineRequest) {
        const res = await window.licenseAPI.exportOfflineRequest(key);
        if (res.ok && res.fileContent) {
          const blob = new Blob([res.fileContent], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `mesh-activation-${key.slice(0, 8)}.mreq`;
          a.click();
          URL.revokeObjectURL(url);
          setSuccessMsg('Offline activation request (.mreq) downloaded. Upload to activation.herx.org/offline');
        } else {
          setErrorMsg(res.error || 'Failed to export offline request');
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Offline export error';
      setErrorMsg(msg);
    }
  };

  const handleImportOfflineLic = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const content = ev.target?.result as string;
      if (!content) return;
      try {
        if (window.licenseAPI?.importOfflineLicense) {
          const res = await window.licenseAPI.importOfflineLicense(content);
          if (res.ok) {
            setSuccessMsg('Offline license (.mlic) imported and verified successfully!');
            await fetchStatus();
          } else {
            setErrorMsg(res.error || 'Invalid offline license file.');
          }
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to import license file';
        setErrorMsg(msg);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in">
      <div className="relative w-full max-w-2xl rounded-2xl border border-slate-700/60 bg-[#0f172a] p-6 shadow-2xl text-slate-100 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400 font-bold">
              ⬡
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-100">Sovereign Node Licensing</h2>
              <p className="text-xs text-slate-400">Node ID &amp; Capability Entitlements</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Current Mode Banner */}
        <div className="mb-5 flex items-center justify-between rounded-xl bg-slate-900/90 border border-slate-800 p-3.5 px-4 text-xs">
          <div className="flex items-center gap-2.5">
            <span className="text-slate-400">Status:</span>
            <span
              className={`font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full text-[10px] ${
                status?.valid
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                  : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
              }`}
            >
              {status?.mode || 'EVALUATING'}
            </span>
          </div>
          <div className="text-slate-400">
            {status?.expiresAt ? `Renews: ${new Date(status.expiresAt).toLocaleDateString()}` : 'Node License'}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 border-b border-slate-800/80 mb-5">
          <button
            onClick={() => setActiveTab('upgrade')}
            className={`pb-2.5 px-3 text-xs font-semibold tracking-wide transition-all border-b-2 ${
              activeTab === 'upgrade'
                ? 'border-sky-400 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Upgrade &amp; Subscribe
          </button>
          <button
            onClick={() => setActiveTab('key')}
            className={`pb-2.5 px-3 text-xs font-semibold tracking-wide transition-all border-b-2 ${
              activeTab === 'key'
                ? 'border-sky-400 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Enter License Key
          </button>
          <button
            onClick={() => setActiveTab('offline')}
            className={`pb-2.5 px-3 text-xs font-semibold tracking-wide transition-all border-b-2 ${
              activeTab === 'offline'
                ? 'border-sky-400 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Air-Gapped Offline
          </button>
        </div>

        {/* Alerts */}
        {errorMsg && (
          <div className="mb-4 rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-xs text-rose-300">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-300">
            {successMsg}
          </div>
        )}

        {/* Tab 1: Upgrade Plans */}
        {activeTab === 'upgrade' && (
          <div className="space-y-4">
            {/* Payment Method Selector */}
            <div className="flex items-center justify-between rounded-xl bg-slate-900/60 p-3 border border-slate-800">
              <div className="text-xs text-slate-300 font-medium">Payment Rail:</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedRail('stripe')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    selectedRail === 'stripe'
                      ? 'bg-sky-500/20 text-sky-400 border border-sky-500/40'
                      : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  💳 Card / FPX
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRail('tng')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    selectedRail === 'tng'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                      : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  Touch 'n Go
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRail('grabpay')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    selectedRail === 'grabpay'
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                      : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  GrabPay
                </button>
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-[11px] font-medium text-slate-400 mb-1">
                Account Email for Billing &amp; Receipts:
              </label>
              <input
                type="email"
                placeholder="operator@sovereign.mesh"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="w-full rounded-lg bg-slate-900 border border-slate-700/80 px-3.5 py-2 text-xs text-slate-100 placeholder-slate-500 focus:border-sky-400 focus:outline-none"
              />
            </div>

            {/* Plan Cards Grid */}
            <div className="grid grid-cols-3 gap-3 pt-2">
              {TIERS.map((tier) => (
                <div
                  key={tier.id}
                  className={`relative flex flex-col justify-between rounded-xl border p-4 transition-all ${
                    tier.recommended
                      ? 'bg-slate-800/60 border-sky-500/50 shadow-lg shadow-sky-500/5'
                      : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {tier.recommended && (
                    <span className="absolute -top-2.5 right-3 rounded-full bg-sky-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-950">
                      Recommended
                    </span>
                  )}
                  <div>
                    <h3 className="text-sm font-bold text-slate-100">{tier.name}</h3>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-lg font-black text-sky-400">
                        {selectedRail === 'stripe' ? tier.priceUsd : tier.priceMyr}
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] text-slate-400">{tier.devices} Active Nodes</p>
                    <ul className="mt-3 space-y-1.5 border-t border-slate-800/80 pt-2.5 text-[10px] text-slate-300">
                      {tier.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <span className="text-emerald-400">✓</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleUpgradeCheckout(tier)}
                    className={`mt-4 w-full rounded-lg py-2 text-xs font-bold transition-all ${
                      tier.recommended
                        ? 'bg-sky-500 text-slate-950 hover:bg-sky-400'
                        : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                    }`}
                  >
                    {isLoading ? 'Connecting...' : `Upgrade ${tier.name}`}
                  </button>
                </div>
              ))}
            </div>

            {/* Special Deals Banner (Lifetime $990 & Pre-Release Annual) */}
            <div className="pt-2">
              <div className="text-xs font-bold text-slate-200 mb-2 flex items-center justify-between">
                <span>🔥 Limited Founder &amp; Pre-Release Offers</span>
                <span className="text-[10px] text-amber-400 font-normal">Capped at 500 licenses</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* Lifetime Deal */}
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3.5 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-amber-300">Lifetime Premium</span>
                      <span className="text-[9px] bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded font-mono font-bold">ONE-TIME</span>
                    </div>
                    <div className="text-base font-black text-slate-100 mt-1">$990 <span className="text-[10px] text-slate-400 font-normal">perpetual</span></div>
                    <p className="text-[10px] text-slate-300 mt-1">Unlimited Pro updates, Local AI &amp; POS, 10 devices. Zero recurring SaaS fees.</p>
                  </div>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => {
                      if (!userEmail) {
                        setErrorMsg('Please enter your account email first.');
                        return;
                      }
                      window.open(`${serviceUrl}/api/v1/billing/checkout?rail=stripe&plan=LIFETIME&email=${encodeURIComponent(userEmail)}`, '_blank');
                    }}
                    className="mt-3 w-full rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 py-1.5 text-xs font-bold text-slate-950 hover:opacity-90 transition-opacity"
                  >
                    Get Lifetime ($990)
                  </button>
                </div>

                {/* Pre-Release Annual Deal */}
                <div className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-3.5 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-sky-300">Pioneer Annual</span>
                      <span className="text-[9px] bg-sky-400/20 text-sky-300 px-1.5 py-0.5 rounded font-mono font-bold">+3 MO FREE</span>
                    </div>
                    <div className="text-base font-black text-slate-100 mt-1">$490 <span className="text-[10px] text-slate-400 font-normal">/ 15 months</span></div>
                    <p className="text-[10px] text-slate-300 mt-1">Pay for 1 year, get 15 full months access ($32.66/mo effective rate).</p>
                  </div>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => {
                      if (!userEmail) {
                        setErrorMsg('Please enter your account email first.');
                        return;
                      }
                      window.open(`${serviceUrl}/api/v1/billing/checkout?rail=stripe&plan=PRE_RELEASE_ANNUAL&email=${encodeURIComponent(userEmail)}`, '_blank');
                    }}
                    className="mt-3 w-full rounded-lg bg-gradient-to-r from-sky-400 to-blue-500 py-1.5 text-xs font-bold text-slate-950 hover:opacity-90 transition-opacity"
                  >
                    Get 15 Mo ($490)
                  </button>
                </div>
              </div>
            </div>

            {/* Referral Draw Section */}
            <div className="pt-2">
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.08) 0%, rgba(18, 24, 36, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  color: '#f8fafc',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#38bdf8', letterSpacing: '0.05em' }}>
                    🎁 FOUNDER DRAW ACTIVE
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Win 1 Full Year of Pro ($948 value)
                  </span>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '0 0 12px 0' }}>
                  Every friend who subscribes gives <strong style={{ color: '#f8fafc' }}>both of you 1 month free</strong> + an entry to win 1 Year of Pro.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const inviteUrl = `${window.location.origin}?ref=${encodeURIComponent(userEmail || 'founder')}`;
                    navigator.clipboard?.writeText(inviteUrl);
                    setSuccessMsg('Referral link copied to clipboard!');
                  }}
                  className="w-full rounded-lg bg-sky-500/20 border border-sky-500/40 py-2 text-xs font-bold text-sky-300 hover:bg-sky-500/30 transition-colors"
                >
                  📋 Copy Your Referral Invite Link
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Manual License Key */}
        {activeTab === 'key' && (
          <div className="space-y-4">
            <p className="text-xs text-slate-400">
              Enter your preset 16-character license key issued by Sovereign Mesh to bind this device online.
            </p>
            <div>
              <label className="block text-[11px] font-medium text-slate-400 mb-1">
                License Key
              </label>
              <input
                type="text"
                placeholder="SOV-XXXX-XXXX-XXXX-XXXX"
                value={licenseKeyInput}
                onChange={(e) => setLicenseKeyInput(e.target.value)}
                className="w-full rounded-lg bg-slate-900 border border-slate-700/80 px-3.5 py-2.5 font-mono text-xs text-sky-400 uppercase placeholder-slate-600 focus:border-sky-400 focus:outline-none"
              />
            </div>
            <button
              type="button"
              disabled={isLoading || !licenseKeyInput}
              onClick={handleActivateOnline}
              className="w-full rounded-lg bg-sky-500 py-2.5 text-xs font-bold text-slate-950 hover:bg-sky-400 disabled:opacity-50 transition-all"
            >
              {isLoading ? 'Verifying with Server…' : 'Activate Node Online'}
            </button>
          </div>
        )}

        {/* Tab 3: Air-Gapped Offline */}
        {activeTab === 'offline' && (
          <div className="space-y-4">
            <p className="text-xs text-slate-400">
              For air-gapped or high-security deployments without Internet egress:
            </p>

            <div className="rounded-xl bg-slate-900/80 border border-slate-800 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-800 text-[10px] font-bold text-sky-400">
                  1
                </span>
                <span className="text-xs font-semibold text-slate-200">Export Activation Request</span>
              </div>
              <input
                type="text"
                placeholder="Enter License Key (SOV-...)"
                value={licenseKeyInput}
                onChange={(e) => setLicenseKeyInput(e.target.value)}
                className="w-full rounded-lg bg-slate-950 border border-slate-800 px-3 py-2 font-mono text-xs text-slate-200"
              />
              <button
                type="button"
                onClick={handleExportOfflineReq}
                className="w-full rounded-lg bg-slate-800 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-700"
              >
                Export Request (.mreq)
              </button>
            </div>

            <div className="rounded-xl bg-slate-900/80 border border-slate-800 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-800 text-[10px] font-bold text-emerald-400">
                  2
                </span>
                <span className="text-xs font-semibold text-slate-200">Import Signed License File</span>
              </div>
              <p className="text-[11px] text-slate-400">
                Upload your request on <code>activation.herx.org/offline</code> via mobile/USB, then import the resulting <code>.mlic</code> file:
              </p>
              <input
                type="file"
                accept=".mlic,application/json"
                onChange={handleImportOfflineLic}
                className="w-full text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-500/20 file:text-emerald-400 hover:file:bg-emerald-500/30 cursor-pointer"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
